/**
 * Pure helpers for deciding whether the current user is the assignee of a resource
 * (e.g. a case record).
 *
 * Identity is determined by the canonical user id (`assigned_user_id`) whenever it
 * is known on both sides, falling back to the deprecated denormalized `assigned`
 * email only when an id comparison is not possible. The email field is denormalized
 * and goes stale (e.g. when a user's email changes, or when case-dispatch
 * auto-assignment sets only the id), so it must never override a definitive id match.
 */

export interface AssignmentCheck {
  /** Logged-in user's id (canonical). */
  currentUserId?: string | null;
  /** Logged-in user's email (deprecated fallback). */
  currentUserEmail?: string | null;
  /** Resource's `assigned_user_id` (canonical). */
  assignedUserId?: string | null;
  /** Resource's deprecated `assigned` email. */
  assignedEmail?: string | null;
}

/**
 * Returns true when the current user should be treated as the assignee.
 *
 * - No assignment at all (neither id nor email) → true (anyone can act).
 * - Both assigned id and current id known → strict id comparison (authoritative).
 * - Otherwise → fall back to email comparison; false if the current user is unknown.
 */
export function isUserAssigned({
  currentUserId,
  currentUserEmail,
  assignedUserId,
  assignedEmail,
}: AssignmentCheck): boolean {
  // No assignment at all → anyone can act.
  if (!assignedUserId && !assignedEmail) {
    return true;
  }

  // Prefer the canonical user id comparison when both ids are known.
  if (assignedUserId && currentUserId) {
    return assignedUserId === currentUserId;
  }

  // Fall back to the deprecated email comparison.
  if (!currentUserEmail) {
    return false;
  }
  return assignedEmail === currentUserEmail;
}
